/**
 * 把播放引擎接进 React，并把它的三种能力接到**真实的浏览器**上。
 *
 * 三种能力在真实环境里的实现：
 *
 * | 能力 | 实现 | 关键注意点 |
 * | --- | --- | --- |
 * | `speech` | `window.speechSynthesis` + `SpeechSynthesisUtterance` | 只吃**文字**；`lang='zh-CN'`；`rate` 放慢到 0.85（听写要听清） |
 * | `audio` | 一个复用的 `new Audio()` | 录音是 `data:` URL（先取回来再放），**不受 Authorization 头限制** |
 * | `clock` | `setTimeout` + `Date.now()` | 见 `shared/dictation/playback.ts` 的纯逻辑 |
 *
 * ## 为什么录音要"先取回来再放"（而不是直接把接口路径喂给 `<audio src>`）
 *
 * 因为接口要 `Authorization: Bearer`，而 `<audio src>` **不会**带自定义请求头。
 * 所以：
 *   1. 进入播放器时按需取一次录音（`GET .../audios/:wordId` → `{ dataUrl }`）；
 *   2. 缓存进内存（一节课最多几十个词、每个几十 KB）；
 *   3. 播放时把 `dataUrl` 交给 `new Audio()`。
 *
 * 这条路顺带解决了"音频接口不能带 token"这个通用问题，且**不需要给录音开一个
 * 公开端点**（那等于把孩子的听写内容暴露给任何拿到 URL 的人）。
 *
 * ## ⚠️ 浏览器自动播放限制
 *
 * `speechSynthesis.speak()` 与 `audio.play()` 都必须发生在**用户手势之后**。
 * 这一层不做任何"自动开始"：引擎的 `gesture` 标志由用户点「开始播放」时打开
 * （见 `DictationPlayer.start()`），这里只负责把点击转成 `start()`。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DictationTempoConfig } from '@fht/shared';
import {
  DictationPlayer,
  realClock,
  type AudioEngine,
  type PlayerQuestion,
  type PlayerSnapshot,
  type SpeechEngine,
} from './dictationPlayer';
import { isSpeechSynthesisSupported, pickChineseVoice } from './speechSynthesis';

/** 中文语音的偏好（`speechSynthesis` 只吃文字，但可以选择音色/语速）。 */
const SPEECH_LANG = 'zh-CN';
/** 听写的语速要**比正常慢**：孩子要听清声调。 */
const SPEECH_RATE = 0.85;

/**
 * 浏览器有没有语音合成（没有时界面要说明，而不是让家长以为"点了没反应"）。
 *
 * ⚠️ 实现在 `./speechSynthesis.ts`（与导入预览的「按顺序朗读」**共用一份**）。
 *    这里保留这个名字是因为 `speechSupported` 早就作为返回值暴露给页面了。
 */
export const isSpeechSupported = isSpeechSynthesisSupported;

/** 真实语音合成。 */
function createSpeechEngine(): SpeechEngine {
  return {
    speak: (text, handlers) => {
      if (!isSpeechSupported() || text === '') {
        // 没有语音合成（或空文本）：立刻"念完"，让时间线照节奏往下走。
        // 不这样做的话，一个不支持的浏览器会让整轮听写**卡在第一格**。
        handlers.onEnd();
        return;
      }
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = SPEECH_LANG;
      utterance.rate = SPEECH_RATE;
      const voice = pickChineseVoice();
      if (voice !== null) utterance.voice = voice;
      utterance.onend = () => handlers.onEnd();
      utterance.onerror = () => handlers.onError();
      try {
        window.speechSynthesis.speak(utterance);
      } catch {
        handlers.onError();
      }
    },
    cancel: () => {
      if (isSpeechSupported()) {
        try {
          window.speechSynthesis.cancel();
        } catch {
          /* 忽略：取消失败不影响后续 */
        }
      }
    },
    isAvailable: () => isSpeechSupported(),
  };
}

/** 真实的录音播放（一个复用的 `Audio` + 内存缓存）。 */
function createAudioEngine() {
  let element: HTMLAudioElement | null = null;
  const engine: AudioEngine = {
    play: (src, handlers) => {
      engine.stop();
      try {
        element = new Audio(src);
      } catch {
        handlers.onError();
        return;
      }
      const current = element;
      current.onended = () => handlers.onEnded();
      current.onerror = () => handlers.onError();
      void current.play().catch(() => {
        /**
         * ⚠️ `play()` 被拒（没有用户手势 / 浏览器不支持这个格式）**不能卡住整轮**：
         * 报错让引擎提前结束这一格。家长会觉得"这一遍没读出来"，但听写还能继续，
         * 而且界面上的来源标签写着"家长录音"——他看得出是哪一段没放出来。
         */
        handlers.onError();
      });
    },
    stop: () => {
      if (element !== null) {
        element.onended = null;
        element.onerror = null;
        try {
          element.pause();
          element.currentTime = 0;
        } catch {
          /* 忽略 */
        }
        element = null;
      }
    },
  };
  return engine;
}

export interface UseDictationPlayerOptions {
  questions: readonly PlayerQuestion[];
  tempo: DictationTempoConfig;
  /** 取一段录音（返回 `data:` URL；没有录音返回 `null`）。 */
  loadAudio: (wordId: string) => Promise<string | null>;
  /** 进度上报（已按题去重）。 */
  markProgress: (questionId: string, status: 'played' | 'skipped') => void;
  /** 整轮结束的回调（用来把会话标成已完成）。 */
  onFinish?: () => void;
}

export interface UseDictationPlayerResult {
  snapshot: PlayerSnapshot;
  start: () => void;
  toggle: () => void;
  pause: () => void;
  replayCurrent: () => void;
  skipCurrent: () => void;
  seek: (position: number) => void;
  stop: () => void;
  speechSupported: boolean;
  /** 已经准备好 data URL 的录音（键是 `wordId`）。 */
  audioReady: Record<string, string>;
  audioLoading: boolean;
  audioError: string | null;
}

/**
 * 播放器 hook。
 *
 * 引擎实例与录音缓存都挂在 `useRef` 上（不随渲染重建）：重建引擎会把
 * "已经播到第几格"弄丢，而家长只是切了一下标签页就不该从头再来。
 */
export function useDictationPlayer(options: UseDictationPlayerOptions): UseDictationPlayerResult {
  const { questions, tempo, loadAudio, markProgress, onFinish } = options;

  /**
   * 把最新的回调放进 ref：引擎只建一次（`useMemo` 的依赖刻意只有题目与节奏），
   * 若把 `markProgress` 放进依赖，父组件每次渲染都会重建引擎、播放位置就丢了。
   * 这是本项目"用 ref 兜住会变的回调"的既有写法（见 `useServerTimer`）。
   */
  const markRef = useRef(markProgress);
  markRef.current = markProgress;
  const finishRef = useRef(onFinish);
  finishRef.current = onFinish;
  const loadAudioRef = useRef(loadAudio);
  loadAudioRef.current = loadAudio;

  const speech = useMemo(() => createSpeechEngine(), []);
  const audio = useMemo(() => createAudioEngine(), []);

  const [audioReady, setAudioReady] = useState<Record<string, string>>({});
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);

  const player = useMemo(
    () =>
      new DictationPlayer(questions, tempo, {
        clock: realClock,
        speech,
        audio,
        markProgress: (questionId, status) => markRef.current(questionId, status),
      }),
    // ⚠️ 只依赖"题目集合与节奏"：换一节课才重建引擎。
    //    `questions` 由调用方按会话 id 记忆化（见 SessionPlayPage）。
    [questions, tempo, speech, audio],
  );

  const [snapshot, setSnapshot] = useState<PlayerSnapshot>(() => player.getSnapshot());

  useEffect(() => {
    const unsubscribe = player.subscribe(() => setSnapshot(player.getSnapshot()));
    return () => {
      unsubscribe();
      player.dispose();
    };
  }, [player]);

  /** 整轮结束时通知调用方（只通知一次）。 */
  const notifiedRef = useRef(false);
  useEffect(() => {
    if (snapshot.finished && !notifiedRef.current) {
      notifiedRef.current = true;
      finishRef.current?.();
    }
    if (!snapshot.finished) notifiedRef.current = false;
  }, [snapshot.finished]);

  /**
   * 按需把家长录音取回内存。
   *
   * 时机：会话详情拿到之后**一次**把它们取回来（一节课几十个词、每段几十 KB，
   * 一部手机完全放得下），而不是"每一格临时去取"—— 后者会在词与词之间
   * 插进一段网络等待，把节奏彻底打乱（听写的节奏是**不能**被网络影响的）。
   */
  useEffect(() => {
    const targets = questions.filter(
      (question) => question.spokenSource === 'parent_audio' && question.wordId !== null,
    );
    if (targets.length === 0) {
      setAudioReady({});
      setAudioError(null);
      return;
    }
    let cancelled = false;
    setAudioLoading(true);
    setAudioError(null);
    void (async () => {
      const next: Record<string, string> = {};
      const failed: string[] = [];
      for (const question of targets) {
        const wordId = question.wordId;
        if (wordId === null) continue;
        try {
          const dataUrl = await loadAudioRef.current(wordId);
          if (dataUrl !== null) next[wordId] = dataUrl;
          else failed.push(question.wordText);
        } catch {
          failed.push(question.wordText);
        }
      }
      if (cancelled) return;
      setAudioReady(next);
      setAudioLoading(false);
      /**
       * ⚠️ **不静默**：取不到的录音要说出来（"这几个词的录音没取到，会用浏览器语音读"）。
       *    否则家长会以为"我明明录过，为什么还是机器音"。
       */
      setAudioError(
        failed.length === 0
          ? null
          : `有 ${failed.length} 个词的录音没取到（${failed.slice(0, 3).join('、')}${
              failed.length > 3 ? ' 等' : ''
            }），这一轮会用浏览器语音读。`,
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [questions]);

  const start = useCallback(() => player.start(), [player]);
  const pause = useCallback(() => player.pause(), [player]);
  const toggle = useCallback(() => player.toggle(), [player]);
  const replayCurrent = useCallback(() => player.replayCurrent(), [player]);
  const skipCurrent = useCallback(() => player.skipCurrent(), [player]);
  const seek = useCallback((position: number) => player.seek(position), [player]);
  const stop = useCallback(() => player.stop(), [player]);

  return {
    snapshot,
    start,
    toggle,
    pause,
    replayCurrent,
    skipCurrent,
    seek,
    stop,
    speechSupported: isSpeechSupported(),
    audioReady,
    audioLoading,
    audioError,
  };
}

/**
 * 把一个会话详情里的题目映射成引擎要的 `PlayerQuestion` 数组。
 *
 * ⚠️ **必须记忆化**（放在 `useMemo` 里）：每次渲染都造一个新数组会让
 *   `useDictationPlayer` 里的引擎被重建，播放位置就丢了。
 */
export function toPlayerQuestions(
  questions: readonly {
    id: string;
    position: number;
    wordId: string | null;
    wordText: string;
    spokenSource: 'parent_audio' | 'tts';
    spokenText: string;
    audioDurationMs: number | null;
    audioPath: string | null;
  }[],
): PlayerQuestion[] {
  return questions.map((question) => ({
    id: question.id,
    position: question.position,
    wordId: question.wordId,
    wordText: question.wordText,
    spokenSource: question.spokenSource,
    spokenText: question.spokenText,
    audioDurationMs: question.audioDurationMs,
    audioPath: question.audioPath,
  }));
}
